import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import '../suboutRegistration/suboutRegistration.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { FormReportsMenu } from '../../../components/FormReportsMenu';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useNavActions } from '../../../hooks/useNavActions';
import { DateRangeSelect, TRACKING_RANGES } from '../../modules/DateRangeSelect';
import { DEFAULT_DATE_RANGE } from '../../../components/listDefaults';
import { fmtLocalDate } from '../../../utils/datetime';
import { apiMessage } from '../suboutRegistration/api';
import { deleteSuboutResultOrders, fetchSuboutResultsList, fetchSuboutResultsLookups } from './api';
import SuboutResultsDetail from './SuboutResultsDetail';
import type { SuboutResultsListRow, SuboutResultsLookups, SuboutResultsMode, SuboutResultsView } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Sample SubContract Tracking → the three G5 lists after Test Order, one component:
//   entry       Subcontract Result Entry      (nav suboutresentry) — Date Filter · Delete
//               · Refresh · Export; a row opens the Result Entry detail.
//   validation  Subcontract Result Validation (nav suboutresval)   — History · Refresh
//               · Export; a row opens the Review detail; History lists past orders.
//   po          Subcontract PO Tracking       (nav suboutpo)       — Date Filter · Refresh
//               · Export; read-only, rows do not open (G5 cannot open them either).
// Columns, captions and order are G5's list views. SQL: AI_SuboutResults_List_SP.
// ─────────────────────────────────────────────────────────────────────────────

type ListKind = 'entry' | 'validation' | 'po';
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | 'All';

const PAGE: Record<ListKind, { pageId: string; form: string; file: string }> = {
  entry:      { pageId: 'suboutresentry', form: 'Subout Sample Result Entry',      file: 'subcontract-result-entry' },
  validation: { pageId: 'suboutresval',   form: 'Subout Sample Result Validation', file: 'subcontract-result-validation' },
  po:         { pageId: 'suboutpo',       form: '',                                file: 'subcontract-po-tracking' },
};

function sinceFromKey(k: DateRangeKey): string | null {
  if (k === 'All') return null;
  const days = TRACKING_RANGES.find((r) => r.label === k)?.days ?? 90;
  const d = new Date(Date.now() - days * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const fd = (v?: string | null) => fmtLocalDate(v) || '';
const t = (field: keyof SuboutResultsListRow, header: string, width: number): AppColumn<SuboutResultsListRow> =>
  ({ field: field as string, header, width, text: (r) => (r[field] as string | null) ?? '' });
const date = (field: keyof SuboutResultsListRow, header: string, width = 110): AppColumn<SuboutResultsListRow> =>
  ({ field: field as string, header, width, type: 'date', text: (r) => fd(r[field] as string | null) });
const status: AppColumn<SuboutResultsListRow> = { field: 'statusText', header: 'Status', width: 210, type: 'status', text: (r) => r.statusText ?? '' };
const statusId = t('statusId', 'StatusID', 80);

const COLUMNS: Record<ListKind, AppColumn<SuboutResultsListRow>[]> = {
  // G5 SubOutSampleRegistrations_ListView_Copy_ResultEntry
  entry: [
    t('jobIds', 'JobID', 120), t('suboutOrderId', 'SuboutOrderID', 130), t('division', 'Division', 200),
    t('contractLab', 'ContractLab', 190), date('dueDate', 'DueDate'), t('deliveryService', 'DeliveryService', 140),
    { field: 'daysElapsed', header: 'DaysElapsed', width: 100, type: 'number', text: (r) => r.daysElapsed == null ? '' : String(r.daysElapsed), value: (r) => r.daysElapsed ?? 0 },
    status, statusId,
  ],
  // G5 SubOutSampleRegistrations_ListView_Level2DataReview
  validation: [
    t('jobIds', 'JobID', 120), t('suboutOrderId', 'SuboutOrderID', 130), t('division', 'Division', 200),
    date('dateShipped', 'DateShipped'), t('trackingNo', 'Tracking#', 130),
    { field: 'numberOfSamples', header: '#Samples', width: 80, type: 'number', text: (r) => String(r.numberOfSamples), value: (r) => r.numberOfSamples },
    t('contractLab', 'ContractLab', 190), t('labAddress', 'Address', 190), t('turnAroundTime', 'TurnAroundTime', 120),
    date('dueDate', 'DueDate'), status, statusId,
  ],
  // G5 SubOutSampleRegistrations_ListView_POTracking (Status = the old SuboutStatus enum)
  po: [
    t('poId', 'PO#', 120), t('suboutOrderId', 'SuboutOrderID', 130), t('total', 'TotalAmount', 110),
    date('dateShipped', 'DateShipped'), t('testRequested', 'TestRequested', 220),
    { field: 'numberOfSamples', header: '#Samples', width: 80, type: 'number', text: (r) => String(r.numberOfSamples), value: (r) => r.numberOfSamples },
    t('contractLab', 'ContractLab', 190), t('labAddress', 'Address', 190), t('turnAroundTime', 'TurnAroundTime', 120),
    date('dueDate', 'DueDate'), { field: 'legacyStatus', header: 'Status', width: 190, type: 'status', text: (r) => r.legacyStatus ?? '' },
  ],
};

export default function SuboutResultsListPage({ kind }: { kind: ListKind }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const page = PAGE[kind];
  const perm = useNavActions(page.pageId);

  const [history, setHistory] = useState(false);
  const view: SuboutResultsView = kind === 'validation' && history ? 'history' : kind;
  const hasDateFilter = kind !== 'validation';

  const [rows, setRows]       = useState<SuboutResultsListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SuboutTracking as DateRangeKey);
  const [search, setSearch]   = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [busy, setBusy]       = useState(false);
  const [openId, setOpenId]   = useState<string | null>(null);
  const [lookups, setLookups] = useState<SuboutResultsLookups | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      setRows(await fetchSuboutResultsList(view, hasDateFilter ? sinceFromKey(dateRange) : null));
      setSelected(new Set());
    } catch (e) {
      notify.error(apiMessage(e, 'Failed to load the subcontract orders.'));
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [view, hasDateFilter, dateRange, notify]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (kind === 'po') return;
    fetchSuboutResultsLookups().then(setLookups).catch(() => {/* editors fall back to typing */});
  }, [kind]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.suboutOrderId, r.jobIds, r.contractLab, r.trackingNo, r.poId, r.testRequested, r.statusText]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  async function remove() {
    if (!perm.delete) { notify.warning('You do not have permission to delete subcontract orders.'); return; }
    const oids = [...selected].map(String);
    if (oids.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    if (!await confirm.confirm({ title: 'Delete', tone: 'danger',
      message: `Delete ${oids.length} subcontract order${oids.length === 1 ? '' : 's'}? Their samples return to Pending Subcontract.` })) return;
    setBusy(true);
    try {
      const r = await deleteSuboutResultOrders(oids);
      const skipped = r.rows.filter((x) => !x.ok);
      if (r.deleted === 0) notify.warning(skipped[0]?.outcome ?? 'Nothing was deleted.');
      else if (skipped.length) notify.warning(`Deleted ${r.deleted}. ${skipped.length} skipped: ${skipped[0]?.outcome ?? ''}`);
      else await confirm.alert({ title: 'Success', tone: 'success', message: 'Deleted successfully.' });
      await load();
    } catch (e) {
      notify.error(apiMessage(e, 'The delete failed.'));
    } finally { setBusy(false); }
  }

  if (openId && kind !== 'po') {
    const mode: SuboutResultsMode = kind === 'entry' ? 'entry' : history ? 'history' : 'validation';
    return (
      <SuboutResultsDetail
        key={openId}
        orderId={openId}
        mode={mode}
        lookups={lookups}
        canWrite={perm.write}
        siblings={visible.map((r) => r.id)}
        onNavigate={setOpenId}
        // Reload on every close: a Review / Submit that did not finish the order still
        // moves rows on, and the order may no longer belong on this list.
        onClose={() => { setOpenId(null); void load(); }}
        onChanged={() => void load()}
      />
    );
  }

  const opens = kind !== 'po';
  const cols: AppColumn<SuboutResultsListRow>[] = opens
    ? [{
        field: 'open', header: '', width: 38, align: 'center', noExport: true, sortable: false, filterable: false,
        render: (r) => (
          <button type="button" className="srd-rowedit" title="Open"
                  onClick={(ev) => { ev.stopPropagation(); setOpenId(r.id); }}>
            <i className="ti ti-edit" aria-hidden="true" />
          </button>
        ),
      }, ...COLUMNS[kind]]
    : COLUMNS[kind];

  return (
    <div className="bl-page">
      <FormTitle pageId={page.pageId} loadMs={loadMs} loading={loading} auditFormName={page.form || undefined}
                 title={kind === 'validation' && history ? 'Subcontract Result Validation — History' : undefined} />

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                {/* G5 Reports ▾ on PO Tracking: the lab's Form Report Permission menu
                    (SOPurchaseOrder on the test database) for the ticked orders. */}
                {kind === 'po' && (
                  <FormReportsMenu
                    reportsUrl="/api/v1/subout-reports?view=po-tracking"
                    pdfUrl="/api/v1/subout-reports/pdf?view=po-tracking"
                    keysParam="orders"
                    selectedKeys={visible.filter((r) => selected.has(r.id) && r.suboutOrderId)
                                         .map((r) => r.suboutOrderId as string)}
                  />
                )}
                {kind === 'validation' && (
                  <button type="button" className={`ca-btn${history ? ' active' : ''}`} onClick={() => setHistory((h) => !h)}
                          title={history ? 'Back to the orders awaiting validation' : 'Orders already validated'}>
                    <i className={`ti ${history ? 'ti-arrow-left' : 'ti-history'}`} aria-hidden="true" /> {history ? 'Pending' : 'History'}
                  </button>
                )}
                {hasDateFilter && (
                  <DateRangeSelect variant="dropdown" label="Date Filter" ranges={TRACKING_RANGES}
                    value={TRACKING_RANGES.find((r) => r.label === dateRange)?.days ?? null}
                    onChange={(d) => setDateRange((TRACKING_RANGES.find((r) => r.days === d)?.label ?? 'All') as DateRangeKey)} />
                )}
              </>
            }
            onDelete={kind === 'entry' ? () => void remove() : undefined}
            deleteDisabled={busy || loading || selected.size === 0}
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: page.file, ...gridExportMatrix(visible, COLUMNS[kind]) }}
            perm={perm}
          />
        </div>

        <div className="list-search-row">
          <GridSearch value={search} onChange={setSearch} placeholder="Search order ID, job, contract lab, tracking…" />
          {selected.size > 0 && <span className="search-match-count">{selected.size} selected</span>}
        </div>

        <div className="bl-card bl-card-flex">
          <AppDataGrid<SuboutResultsListRow>
            rows={visible}
            columns={cols}
            getRowId={(r) => r.id}
            loading={loading}
            loadMs={loadMs}
            selectable={kind === 'entry' || kind === 'po'}
            selectedIds={selected}
            onSelectionChange={setSelected}
            onRowDoubleClick={opens ? (r) => setOpenId(r.id) : undefined}
            emptyText={kind === 'entry' ? 'No subcontract orders are pending result entry.'
              : kind === 'po' ? 'No subcontract orders with a PO number for this date range.'
              : history ? 'No validated subcontract orders.' : 'No subcontract results are awaiting validation.'}
          />
        </div>
      </div>
    </div>
  );
}
